import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useModuleAccess } from './lib/featureFlags';
import { formatSizeFR, formatDateFR } from './components/documents/DocumentsInternesPanel';
import DocumentTemplatesLibrary from './components/documents/DocumentTemplatesLibrary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token') || '';

/**
 * DocumentsView — Hub Documents
 *
 * SOCLE (`documents-base`, toujours actif, sans cadenas) :
 *   - Bibliothèque agrégée : tous les documents internes de l'agence
 *     (recherche, filtre par type / entité, « Expire bientôt »).
 *
 * MODULE IA (`documents-ia`, activable) — affiché UNIQUEMENT si actif :
 *   - Onglet « Générer » + cartes générateurs + CTA « Générer un document ».
 *   Quand le module est OFF : aucun générateur, aucun bouton « Générer »,
 *   aucun écran « Bientôt » — il ne reste que le socle. (Corrige le gel
 *   « chargement infini » constaté chez Cabinet Grimaz.)
 */

// ─── Templates IA (alignés avec DOCUMENT_TEMPLATES dans server.py) ───
const DOC_TEMPLATES = {
  commerciaux: {
    label: 'Documents Commerciaux', icon: 'bi-briefcase', accent: 'emerald',
    items: [
      { id: 'rapport_visite', name: 'Rapport de Visite', desc: 'Avec photos et observations', icon: 'bi-camera' },
      { id: 'annonce', name: 'Annonce Immobilière', desc: 'Description automatique', icon: 'bi-megaphone' },
      { id: 'dossier_presentation', name: 'Dossier Présentation', desc: 'Pour propriétaires / acquéreurs', icon: 'bi-file-earmark-slides' },
    ],
  },
  legaux: {
    label: 'Documents Légaux', icon: 'bi-shield-check', accent: 'amber',
    items: [
      { id: 'mandat_vente', name: 'Mandat de Vente', desc: 'Pré-rempli automatiquement', icon: 'bi-file-earmark-medical' },
      { id: 'contrat_location', name: 'Contrat de Location', desc: 'Clauses personnalisées', icon: 'bi-house-door' },
      { id: 'etat_lieux', name: 'État des Lieux', desc: 'Entrée et sortie', icon: 'bi-clipboard-data' },
    ],
  },
  marketing: {
    label: 'Documents Marketing', icon: 'bi-palette', accent: 'violet',
    items: [
      { id: 'flyer', name: 'Flyer Propriété', desc: 'Avec visuels automatiques', icon: 'bi-image' },
      { id: 'brochure', name: 'Brochure Marketing', desc: 'Présentation professionnelle', icon: 'bi-journal-richtext' },
      { id: 'campagne_emailing', name: 'Campagne Emailing', desc: 'Messages personnalisés', icon: 'bi-envelope-paper' },
    ],
  },
};

const ACCENT_CLASSES = {
  emerald: 'text-emerald-600 bg-emerald-50',
  amber: 'text-amber-600 bg-amber-50',
  violet: 'text-violet-600 bg-violet-50',
};

const ENTITY_META = {
  property: { label: 'Bien', icon: 'bi-house-door', path: (id) => `/properties/${id}/view` },
  contact: { label: 'Contact', icon: 'bi-person', path: (id) => `/contacts/${id}` },
  agence: { label: 'Agence', icon: 'bi-building', path: () => '/admin-agence' },
};

const mimeIcon = (mime, name = '') => {
  const m = (mime || '').toLowerCase(); const n = (name || '').toLowerCase();
  if (m.includes('pdf') || n.endsWith('.pdf')) return { i: 'bi-file-earmark-pdf', c: 'text-rose-500' };
  if (m.includes('image') || /\.(jpe?g|png|webp)$/.test(n)) return { i: 'bi-file-earmark-image', c: 'text-slate-500' };
  if (m.includes('word') || n.endsWith('.docx')) return { i: 'bi-file-earmark-word', c: 'text-blue-600' };
  if (m.includes('sheet') || n.endsWith('.xlsx')) return { i: 'bi-file-earmark-excel', c: 'text-emerald-600' };
  return { i: 'bi-file-earmark', c: 'text-slate-500' };
};

// =============== Bibliothèque agrégée (SOCLE) ===============
const AggregatedLibrary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [expiring, setExpiring] = useState(searchParams.get('expiring') === '1');
  const [threshold, setThreshold] = useState(90);

  useEffect(() => {
    let off = false;
    axios.get(`${API}/document-types`).then((r) => { if (!off) setTypes(r.data || []); }).catch(() => {});
    return () => { off = true; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (typeFilter) params.type_document = typeFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (expiring) params.expiring = true;
      const r = await axios.get(`${API}/documents/all`, { params });
      setItems(r.data?.items || []);
      if (r.data?.threshold_days) setThreshold(r.data.threshold_days);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [q, typeFilter, entityFilter, expiring]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0); // léger debounce sur la recherche
    return () => clearTimeout(t);
  }, [load, q]);

  const typeLabel = (id) => types.find((t) => t.id === id)?.label || id;
  const view = (doc) => {
    const tk = getToken();
    if (!tk) { toast.error('Session expirée, veuillez vous reconnecter.'); return; }
    window.open(`${API}/documents/${doc.id}/download?auth=${encodeURIComponent(tk)}`, '_blank', 'noopener');
  };
  const download = async (doc) => {
    try {
      const r = await axios.get(`${API}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.display_name || 'document';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.status === 401 ? 'Session expirée, veuillez vous reconnecter.' : 'Téléchargement impossible.'); }
  };

  return (
    <div className="space-y-4" data-testid="documents-library">
      {/* Filtres */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un document…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="documents-library-search" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="documents-library-type">
          <option value="">Tous les types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="documents-library-entity">
          <option value="">Toutes les entités</option>
          <option value="property">Biens</option>
          <option value="contact">Contacts</option>
          <option value="agence">Agence</option>
        </select>
        <button onClick={() => setExpiring((v) => !v)}
          className={`px-3 py-2 text-sm font-semibold rounded-lg border transition flex items-center gap-1.5 ${expiring ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`}
          data-testid="documents-library-expiring">
          <i className="bi bi-clock-history" /> Expire bientôt
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-slate-400"><i className="bi bi-arrow-repeat animate-spin text-2xl" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl py-16 text-center" data-testid="documents-library-empty">
          <i className="bi bi-folder2-open text-4xl text-slate-300" />
          <p className="text-sm font-medium text-slate-600 mt-2">{expiring ? 'Aucun diagnostic à renouveler' : 'Aucun document interne'}</p>
          <p className="text-xs text-slate-400 mt-1">Les documents ajoutés depuis les fiches biens et contacts apparaissent ici.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100">
          {items.map((doc) => {
            const ic = mimeIcon(doc.mime_type, doc.filename_original);
            const em = ENTITY_META[doc.entity_type] || ENTITY_META.agence;
            const d = doc.date_expiration ? Math.ceil((new Date(doc.date_expiration) - new Date()) / 86400000) : null;
            return (
              <div key={doc.id} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition" data-testid={`documents-library-row-${doc.id}`}>
                <i className={`bi ${ic.i} ${ic.c} text-2xl shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{doc.display_name}</p>
                  <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1">
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700">{typeLabel(doc.type_document)}</span>
                    <button onClick={() => navigate(em.path(doc.entity_id))} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center gap-1">
                      <i className={`bi ${em.icon}`} /> {em.label}
                    </button>
                    <span className="text-[11px] text-slate-400">{formatSizeFR(doc.size_bytes)}</span>
                    <span className="text-[11px] text-slate-400">· {formatDateFR(doc.uploaded_at)}</span>
                    {d !== null && d < 0 && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">Expiré le {formatDateFR(doc.date_expiration)}</span>}
                    {d !== null && d >= 0 && d <= threshold && <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Expire le {formatDateFR(doc.date_expiration)}</span>}
                  </div>
                </div>
                <button onClick={() => view(doc)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-100 transition" title="Voir"><i className="bi bi-eye" /></button>
                <button onClick={() => download(doc)} className="text-slate-500 hover:text-blue-600 p-1.5 rounded-md hover:bg-slate-100 transition" title="Télécharger"><i className="bi bi-download" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// =============== Modal Génération (IA) ===============
const GenerateModal = ({ template, onClose }) => {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/properties?status=active`).then((r) => { if (!cancelled) setProperties(r.data || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const payload = { document_type: template.id, custom_instructions: instructions || '' };
      if (propertyId) payload.property_id = propertyId;
      const r = await axios.post(`${API}/documents/generate`, payload);
      if (r.data?.success && r.data?.content) {
        setResult(r.data);
      } else {
        toast.error(r.data?.error || 'La génération n\'a renvoyé aucun contenu.');
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Erreur lors de la génération du document.'));
    } finally {
      setGenerating(false); // jamais de chargement infini, quel que soit le retour
    }
  };

  const handleCopy = async () => {
    if (!result?.content) return;
    try { await navigator.clipboard.writeText(result.content); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt('Copier (Ctrl+C) :', result.content); }
  };

  const handleDownload = () => {
    if (!result?.content) return;
    const blob = new Blob([result.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${template.id}_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="documents-generate-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><i className={`bi ${template.icon || 'bi-magic'} text-blue-600 text-lg`} /></div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-800 leading-tight">Générer un document</h3>
              <p className="text-xs text-slate-500 truncate">{template.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition" data-testid="documents-generate-close"><i className="bi bi-x-lg text-lg" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!result && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bien concerné <span className="text-slate-400 font-normal">(optionnel)</span></label>
                <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500" data-testid="documents-generate-property">
                  <option value="">Sélectionnez un bien…</option>
                  {properties.map((p) => {
                    const ville = p.bien_general?.ville_generale || p.localisation?.ville || p.informations_confidentielles?.ville || '';
                    const titre = p.bien_general?.titre_annonce || p.objet || p.profil || 'Bien';
                    return <option key={p.id} value={p.id}>{p.reference ? `${p.reference} · ` : ''}{titre}{ville ? ` · ${ville}` : ''}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Instructions supplémentaires <span className="text-slate-400 font-normal">(optionnel)</span></label>
                <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={5}
                  placeholder="Ajoutez des détails spécifiques, points à mettre en avant, style souhaité…"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none" data-testid="documents-generate-instructions" />
              </div>
            </>
          )}
          {generating && (
            <div className="text-center py-10">
              <i className="bi bi-magic text-4xl text-blue-600 animate-pulse" />
              <p className="mt-3 text-sm font-medium text-slate-700">Génération en cours…</p>
            </div>
          )}
          {result && !generating && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"><i className="bi bi-check-circle-fill" /> Document généré avec succès.</div>
              <textarea value={result.content || ''} readOnly rows={14} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-slate-50 font-mono leading-relaxed" data-testid="documents-generate-result" />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          {!result && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition" data-testid="documents-generate-cancel">Annuler</button>
              <button onClick={handleGenerate} disabled={generating} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center gap-2" data-testid="documents-generate-submit">
                <i className="bi bi-magic" />{generating ? 'Génération…' : 'Générer'}
              </button>
            </>
          )}
          {result && (
            <>
              <button onClick={() => { setResult(null); setInstructions(''); }} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition" data-testid="documents-generate-restart"><i className="bi bi-arrow-clockwise mr-1" />Nouveau</button>
              <button onClick={handleCopy} className="px-4 py-2 text-sm font-medium bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg transition flex items-center gap-2" data-testid="documents-generate-copy"><i className={`bi ${copied ? 'bi-check-lg text-emerald-600' : 'bi-clipboard'}`} />{copied ? 'Copié !' : 'Copier'}</button>
              <button onClick={handleDownload} className="px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center gap-2" data-testid="documents-generate-download"><i className="bi bi-download" />Télécharger</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// =============== Grille des générateurs (IA) ===============
const GeneratorsGrid = ({ onSelect }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="documents-generators">
    {Object.entries(DOC_TEMPLATES).map(([key, cat]) => (
      <div key={key} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <i className={`bi ${cat.icon} text-base ${ACCENT_CLASSES[cat.accent].split(' ')[0]}`} />
          <h3 className="text-sm font-semibold text-slate-800">{cat.label}</h3>
        </div>
        <div className="p-3 space-y-2">
          {cat.items.map((tpl) => (
            <button key={tpl.id} onClick={() => onSelect(tpl)} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-slate-50 transition flex items-center gap-3 group" data-testid={`documents-template-${tpl.id}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${ACCENT_CLASSES[cat.accent]}`}><i className={`bi ${tpl.icon} text-base`} /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 leading-tight">{tpl.name}</div>
                <div className="text-xs text-slate-500 leading-snug mt-0.5">{tpl.desc}</div>
              </div>
              <i className="bi bi-arrow-right text-slate-300 group-hover:text-blue-500 transition" />
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// =============== Composant racine ===============
const DocumentsView = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { enabled: iaEnabled } = useModuleAccess('documents-ia');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const tabs = useMemo(() => {
    const base = [
      { slug: 'bibliotheque', pathSlug: '', label: 'Tous les documents', icon: 'bi-folder-fill', description: 'Documents internes de l\'agence' },
      { slug: 'types', pathSlug: 'types', label: 'Documents types', icon: 'bi-collection', description: 'Modèles réutilisables de l\'agence' },
    ];
    if (iaEnabled) base.push({ slug: 'generer', pathSlug: 'generer', label: 'Générer', icon: 'bi-magic', description: 'Création assistée par IA' });
    return base;
  }, [iaEnabled]);

  const activeTab = useMemo(() => {
    const found = tabs.find((t) => (t.pathSlug || '') === (tab || ''));
    return found || tabs[0];
  }, [tab, tabs]);

  // Si l'utilisateur arrive sur /documents/generer alors que l'IA est OFF → on retombe sur le socle
  useEffect(() => {
    if (!iaEnabled && (tab === 'generer' || tab === 'ajouter' || tab === 'legaux' || tab === 'rapports')) {
      navigate('/documents', { replace: true });
    }
  }, [iaEnabled, tab, navigate]);

  const goTo = (t) => navigate(t.pathSlug ? `/documents/${t.pathSlug}` : '/documents');

  return (
    <div className="p-3 md:p-6 space-y-4 bg-slate-50 min-h-screen" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-100 flex items-center justify-center"><i className="bi bi-file-earmark-text text-blue-600 text-xl sm:text-2xl" /></div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Documents internes</h1>
            <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{activeTab.description}</p>
          </div>
        </div>
        {iaEnabled && (
          <button onClick={() => goTo(tabs[1])} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition flex items-center gap-2" data-testid="documents-generate-btn">
            <i className="bi bi-magic" /><span>Générer un document</span>
          </button>
        )}
      </div>

      {/* Onglets (uniquement si plus d'un) */}
      {tabs.length > 1 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2" role="tablist">
            {tabs.map((t) => {
              const isActive = activeTab.slug === t.slug;
              return (
                <button key={t.slug} onClick={() => goTo(t)} role="tab" aria-selected={isActive} data-testid={`documents-tab-${t.slug}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${isActive ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <i className={`bi ${t.icon} text-xs`} /><span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Contenu */}
      {activeTab.slug === 'generer' && iaEnabled ? (
        <GeneratorsGrid onSelect={setSelectedTemplate} />
      ) : activeTab.slug === 'types' ? (
        <DocumentTemplatesLibrary />
      ) : (
        <AggregatedLibrary />
      )}

      {selectedTemplate && iaEnabled && (
        <GenerateModal template={selectedTemplate} onClose={() => setSelectedTemplate(null)} />
      )}
    </div>
  );
};

export default DocumentsView;
